/**
 * face-cam-extract — CAM yüz sınırı çıkarma (THREE köprüsü, cep+profil ortak).
 *
 * İki tier:
 * - occtFaceVertices: parça STEP shape'inden GERÇEK BREP kontur (exact LINE/ARC).
 * - faceBrepVertices: mesh faceId üçgen bölgesinden sıralı boundary loop (tessellation).
 *
 * Dialoglar (dialog-fiko-cep, dialog-fiko-profil) bunu kullanır → tek kaynak, DRY.
 */

import * as THREE from 'three';
import { sinirLoopCikar, konturSadelestir, duzlemTabani, Ucgen2D, IXY } from './brep-kontur';
import { occtYuzKonturu, occtYuzAdalar } from './occt-cam-kontur';

/** Tıklanan üçgenin faceId'sini (grup attribute z) verir, yoksa null. */
function faceIdAl(geom: THREE.BufferGeometry, faceIndex: number): number | null {
    const grup: any = geom.attributes?.['grup'];
    if (!grup) return null;
    const index = geom.index;
    const triCount = index ? Math.floor(index.count / 3) : Math.floor((geom.attributes['position']?.count || 0) / 3);
    if (faceIndex < 0 || faceIndex >= triCount) return null;
    const vi = index ? index.getX(faceIndex * 3) : faceIndex * 3;
    return grup.getZ(vi);
}

/**
 * OCCT offset için referans: __stepBytes taşıyan parça grubu + faceId.
 * Activity bunu saklayıp sync OCCT offset (occtKonturOffsetSync) çağırır.
 */
export function occtFaceRef(obj: THREE.Object3D, faceIndex: number): { grup: any; faceId: number } | null {
    if (!(obj instanceof THREE.Mesh) || !obj.geometry) return null;
    const faceId = faceIdAl(obj.geometry, faceIndex);
    if (faceId == null) return null;
    let part: any = obj;
    while (part && !part.userData?.['__stepBytes']) part = part.parent;
    if (!part) return null;
    return { grup: part, faceId };
}

/**
 * OCCT GERÇEK BREP: parça STEP shape'inden seçilen yüzün exact konturu (dünya Vector3).
 * pickPoint guard'lı. __stepBytes taşıyan parça yoksa / eşleşmezse null.
 */
export async function occtFaceVertices(
    obj: THREE.Object3D, faceIndex: number, point?: THREE.Vector3
): Promise<THREE.Vector3[] | null> {
    try {
        if (!(obj instanceof THREE.Mesh) || !obj.geometry) return null;
        const faceId = faceIdAl(obj.geometry, faceIndex);
        if (faceId == null) return null;

        let part: any = obj;
        while (part && !part.userData?.['__stepBytes']) part = part.parent;
        if (!part) return null;

        const pts = await occtYuzKonturu(part, faceId);
        if (!pts || pts.length < 3) return null;

        // MODEL → WORLD: mesh transformu uygula (OCCT model coords ↔ mesh local coords hizalı varsayımı)
        obj.updateWorldMatrix(true, false);
        const mw = obj.matrixWorld;
        const dunya = pts.map(p => new THREE.Vector3(p.x, p.y, p.z).applyMatrix4(mw));

        // Pick-guard (world): kontur tıklanan noktaya yakın değilse faceId eşleşmemiş → fallback
        if (point) {
            const box = new THREE.Box3().setFromPoints(dunya).expandByScalar(1); // 1mm pay
            if (!box.containsPoint(point)) { console.warn('[OCCT-CAM] kontur pick noktasina uzak -> fallback'); return null; }
        }
        return dunya;
    } catch {
        return null;
    }
}

/**
 * OCCT yüz iç konturları (delik/ada) → dünya Vector3 loop'ları. Pocket island otomatik tespiti.
 */
export async function occtFaceIslands(obj: THREE.Object3D, faceIndex: number): Promise<THREE.Vector3[][] | null> {
    try {
        if (!(obj instanceof THREE.Mesh) || !obj.geometry) return null;
        const faceId = faceIdAl(obj.geometry, faceIndex);
        if (faceId == null) return null;
        let part: any = obj;
        while (part && !part.userData?.['__stepBytes']) part = part.parent;
        if (!part) return null;
        const adalar = await occtYuzAdalar(part, faceId);
        if (!adalar?.length) return null;
        obj.updateWorldMatrix(true, false);
        const mw = obj.matrixWorld;
        return adalar.map(ada => ada.map(p => new THREE.Vector3(p.x, p.y, p.z).applyMatrix4(mw)));
    } catch {
        return null;
    }
}

/**
 * Mesh-tabanlı: faceId üçgen bölgesini face düzlemine projekte eder, sıralı boundary
 * loop çıkarır + eşdoğrusal sadeleştirir. Dünya Vector3 döndürür (kapalı, ilk≠son).
 */
export function faceBrepVertices(obj: THREE.Object3D, faceIndex: number): THREE.Vector3[] {
    if (!(obj instanceof THREE.Mesh) || !obj.geometry) return [];
    const geom = obj.geometry;
    const pos: any = geom.attributes?.['position'];
    const grup: any = geom.attributes?.['grup'];
    if (!pos || !grup) return [];

    const index = geom.index;
    const triCount = index ? Math.floor(index.count / 3) : Math.floor(pos.count / 3);
    if (faceIndex < 0 || faceIndex >= triCount) return [];
    const vIdx = (t: number, k: number) => index ? index.getX(t * 3 + k) : t * 3 + k;
    const hedefFaceId = grup.getZ(vIdx(faceIndex, 0));

    const mw = obj.matrixWorld;
    obj.updateWorldMatrix(true, false);
    const tris: THREE.Vector3[][] = [];
    for (let t = 0; t < triCount; t++) {
        if (grup.getZ(vIdx(t, 0)) !== hedefFaceId) continue;
        const a = new THREE.Vector3().fromBufferAttribute(pos, vIdx(t, 0)).applyMatrix4(mw);
        const b = new THREE.Vector3().fromBufferAttribute(pos, vIdx(t, 1)).applyMatrix4(mw);
        const c = new THREE.Vector3().fromBufferAttribute(pos, vIdx(t, 2)).applyMatrix4(mw);
        tris.push([a, b, c]);
    }
    if (tris.length === 0) return [];

    const normal = new THREE.Vector3();
    const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), nrm = new THREE.Vector3();
    for (const [a, b, c] of tris) {
        e1.subVectors(b, a); e2.subVectors(c, a); nrm.crossVectors(e1, e2);
        if (normal.lengthSq() > 0 && nrm.dot(normal) < 0) nrm.negate();
        normal.add(nrm);
    }
    if (normal.lengthSq() < 1e-12) return [];
    normal.normalize();

    const tb = duzlemTabani({ x: normal.x, y: normal.y, z: normal.z });
    const U = new THREE.Vector3(tb.u.x, tb.u.y, tb.u.z);
    const V = new THREE.Vector3(tb.v.x, tb.v.y, tb.v.z);
    const origin = tris[0][0].clone();
    const rel = new THREE.Vector3();
    const proj = (p: THREE.Vector3): IXY => { rel.subVectors(p, origin); return { x: rel.dot(U), y: rel.dot(V) }; };

    const ucgenler2d: Ucgen2D[] = tris.map(([a, b, c]) => [proj(a), proj(b), proj(c)]);
    const loops = sinirLoopCikar(ucgenler2d);
    if (loops.length === 0) return [];
    const dis = konturSadelestir(loops[0].noktalar, 0.01);
    if (dis.length < 3) return [];

    return dis.map(p => origin.clone().addScaledVector(U, p.x).addScaledVector(V, p.y));
}
